import React, { createRef } from 'react';
import gsap from 'gsap';

import EventDispatcherModule from '@shcc/modules/EventDispatcherModule';
import { scrollModule } from '@shcc/modules/ScrollModule';
import { resizeModule } from '@shcc/modules/ResizeModule';
import { lerp } from '@shcc/modules/StandardEaseModule';

// Helpers
import { isClient } from '@shcc/utils/DeviceUtils';
import bindAll from '@shcc/utils/helpers/bindAll';
import fixed from '@shcc/utils/helpers/fixed';

// Styles
import styles from './ScrollContainerComponent.module.scss';

class ScrollContainerModule extends EventDispatcherModule {
  constructor() {
    super();

    // Bind Handlers
    bindAll(this, ['_tickHandler', '_scrollHandler']);

    this.active = null;
    this.history = [];
    this.containers = {};

    this._setupEventListeners();
  }

  activate(id, options = {}, historyPushEnabled = true) {
    if (this.active === id) return;

    this.dispatchEvent(`request:active:${id}`, id);
    this.dispatchEvent(`request:active`, id);

    // Push to history
    if (historyPushEnabled) this.history.push(id);

    // Restore
    const container = this.get(id);

    if (!options.restore) {
      this.setPosition(id, { x: 0, y: 0 });
    }

    window.scrollTo(container.position.x, container.position.y);

    this.dispatchEvent('restore', this.active);

    this.active = id;
  }

  deactivate() {
    this.active = false;
  }

  back(options = {}) {
    const previous = this.history[this.history.length - 2];

    if (previous) {
      this.history.pop();
      this.deactivate();
      this.activate(previous, options, false);
    }
  }

  register(id, options = {}) {
    if (this.containers[id]) return; // No more than one, shall be used

    const damping = options.damping || 0.0;
    const position = options.position || { x: 0, y: 0 };

    this.containers[id] = { id, position, damping };
  }

  get(id) {
    return this.containers[id];
  }

  setPosition(id, { x, y }) {
    const container = this.containers[id];

    if (container.position.x !== x || container.position.y !== y) {
      container.position = { x, y };
      this.dispatchEvent(`update:position:${id}`, container.position);
    }
  }

  _setupEventListeners() {
    gsap.ticker.add(this._tickHandler);

    scrollModule.addEventListener('scroll', this._scrollHandler);
  }

  /**
   *
   * Handlers
   */
  _tickHandler() {
    const container = this.containers[this.active];
    if (container && container.damping) {
      this.setPosition(this.active, {
        x: fixed(
          lerp(
            container.position.x,
            scrollModule.position.x,
            container.damping
          ),
          2
        ),
        y: fixed(
          lerp(
            container.position.y,
            scrollModule.position.y,
            container.damping
          ),
          2
        ),
      });
    }
  }

  _scrollHandler() {
    const container = this.containers[this.active];
    if (container && !container.damping) {
      this.setPosition(this.active, {
        x: scrollModule.position.x,
        y: scrollModule.position.y,
      });
    }
  }
}

export const scrollContainerModule = new ScrollContainerModule();

class ScrollContainerComponent extends React.Component {
  ui = {
    content: createRef(),
  };

  constructor(props = {}) {
    super();

    bindAll(this, [
      '_updatePositionHandler',
      '_resizeHandler',
      '_requestActiveHandler',
      '_requestActiveCurrentHandler',
    ]);

    if (isClient()) {
      /**
       *
       * Initialized Scroll Container Module
       * before mount.
       *
       * @todo: Very anti-pattern look into
       * different solution later.
       *
       */
      scrollContainerModule.register(props.name, { damping: props.damping });
    }
  }

  componentDidMount() {
    this._setupEventListeners();

    if (this.props.damping) {
      this._setContentHeight();
      this._setContentOffset();
    }

    if (!this.props.damping) {
      this._setContentPosition(this.props.name);
    }

    scrollContainerModule.activate(this.props.name, {
      restore: this.props.restore,
    });
  }

  componentWillUnmount() {
    this._removeEventListeners();

    // Only if not triggered manually
    if (scrollContainerModule.active === this.props.name) {
      scrollContainerModule.deactivate();
    }
  }

  render() {
    const { children } = this.props;

    return (
      <div className={styles.content} ref={this.ui.content}>
        {children}
      </div>
    );
  }

  _setupEventListeners() {
    resizeModule.addEventListener('resize', this._resizeHandler);
    resizeModule.addEventListener('resize:complete', this._resizeHandler);

    scrollContainerModule.addEventListener(
      `update:position:${this.props.name}`,
      this._updatePositionHandler
    );
    scrollContainerModule.addEventListener(
      `request:active`,
      this._requestActiveHandler
    );
    scrollContainerModule.addEventListener(
      `request:active:${this.props.name}`,
      this._requestActiveCurrentHandler
    );
  }

  _removeEventListeners() {
    resizeModule.removeEventListener('resize', this._resizeHandler);
    resizeModule.removeEventListener('resize:complete', this._resizeHandler);

    scrollContainerModule.removeEventListener(
      `update:position:${this.props.name}`,
      this._updatePositionHandler
    );
    scrollContainerModule.addEventListener(
      `request:active`,
      this._requestActiveHandler
    );
    scrollContainerModule.removeEventListener(
      `request:active:${this.props.name}`,
      this._requestActiveCurrentHandler
    );
  }

  _setContentHeight() {
    const container = scrollContainerModule.get(this.props.name);
    container.height = this.ui.content.current.clientHeight;

    document.body.style.height = `${container.height}px`;
  }

  _setContentOffset() {
    const container = scrollContainerModule.get(this.props.name);
    const offset = container.position.y * -1;

    this.ui.content.current.style.transform = `matrix(1.00, 0.00, 0.00, 1.00, 0, ${offset})`;
  }

  _setContentPosition(active) {
    const container = scrollContainerModule.get(this.props.name);
    const { name } = this.props;

    if (this.ui.content.current) {
      const content = this.ui.content.current;
      const offset = name === active ? 0 : container.position.y * -1;
      const position = name === active ? 'relative' : 'fixed';

      content.style.position = position;
      content.style.transform = `matrix(1.00, 0.00, 0.00, 1.00, 0, ${offset})`;
    }

    // if (active !== this.props.name && this.ui.content.current) {
    //   this.ui.content.current.style.position = 'fixed';
    //   this.ui.content.current.style.transform = `matrix3d(1,0, 0.00, 0, 0.00, 1, 0.00, 0, 0, 0, 1, 0, 0, ${offset}, 0, 1)`;
    //   return;
    // }

    // this.ui.content.current.style.position = 'relative';
  }

  /**
   *
   * Handlers
   */
  _updatePositionHandler() {
    if (this.props.damping) {
      this._setContentOffset();
    }
  }

  _resizeHandler() {
    if (this.props.damping) {
      this._setContentHeight();
      this._setContentOffset();
    }
  }

  _requestActiveCurrentHandler() {
    if (this.props.damping) {
      this._setContentHeight();
      this._setContentOffset();
    }
  }

  _requestActiveHandler(active) {
    if (!this.props.damping) {
      this._setContentPosition(active);
    }
  }
}

export default ScrollContainerComponent;
